import React from "react";

const PrivacyPolicy = () => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-white via-slate-50 to-[#f8f6ef] text-slate-900 px-5 sm:px-8 lg:px-12 py-16">
      <div className="max-w-5xl mx-auto">

        {/* Header */}
        <div className="text-center mb-14">
          <h1 className="text-4xl sm:text-5xl font-black mb-4">
            Privacy Policy
          </h1>

          <p className="text-slate-500">
            Effective Date: May 13, 2026 • Last Updated: 2026
          </p>

          {/* ✅ SEO DESCRIPTION (CONNECTED CONTENT) */}
          <p className="mt-6 text-slate-600 leading-7 max-w-3xl mx-auto">
            This Privacy Policy explains how Just For Points collects, uses, and
            protects your personal information when you use our website,
            travel rewards guides, tools, and related services. We are committed
            to transparency, data protection, and compliance with global privacy laws.
          </p>
        </div>

        {/* Main Card */}
        <div className="bg-white/90 backdrop-blur-xl border border-slate-200 rounded-3xl p-6 sm:p-10 space-y-10 shadow-2xl shadow-black/5">

          {/* 1 */}
          <section>
            <h2 className="text-2xl font-bold mb-4">1. Introduction</h2>
            <p className="text-slate-600 leading-8">
              Just For Points (“we,” “our,” or “us”) operates a travel rewards
              information platform. We respect your privacy and are committed to
              protecting any personal data you share with us.
            </p>
          </section>

          {/* 2 */}
          <section>
            <h2 className="text-2xl font-bold mb-4">2. Information We Collect</h2>

            <ul className="list-disc pl-6 text-slate-600 leading-8 space-y-2">
              <li>Name and email address</li>
              <li>Device type, browser, and IP address</li>
              <li>Approximate location data</li>
              <li>Website usage analytics</li>
              <li>Cookies and session data</li>
              <li>Information you voluntarily submit</li>
            </ul>
          </section>

          {/* 3 */}
          <section>
            <h2 className="text-2xl font-bold mb-4">3. How We Use Your Data</h2>

            <ul className="list-disc pl-6 text-slate-600 leading-8 space-y-2">
              <li>To improve website content and performance</li>
              <li>To provide personalized travel reward insights</li>
              <li>To send newsletters and updates</li>
              <li>To analyze user engagement</li>
              <li>To maintain security and prevent abuse</li>
              <li>To comply with legal obligations</li>
            </ul>
          </section>

          {/* 4 */}
          <section>
            <h2 className="text-2xl font-bold mb-4">
              4. Cookies & Tracking Technologies
            </h2>

            <p className="text-slate-600 leading-8">
              We use cookies and similar technologies to enhance user experience,
              analyze traffic, and deliver relevant content. You can disable
              cookies in your browser settings, but some features may not work
              properly.
            </p>
          </section>

          {/* 5 */}
          <section>
            <h2 className="text-2xl font-bold mb-4">
              5. Advertising (Google AdSense)
            </h2>

            <p className="text-slate-600 leading-8">
              We use Google AdSense and other advertising partners to display ads.
              These partners may use cookies to show personalized or non-personalized ads
              based on your browsing behavior.
            </p>
          </section>

          {/* 6 */}
          <section>
            <h2 className="text-2xl font-bold mb-4">
              6. Email Communications
            </h2>

            <p className="text-slate-600 leading-8">
              If you subscribe to our newsletter or waitlist, you may receive
              email updates. You can unsubscribe anytime using the link provided
              in each email.
            </p>
          </section>

          {/* 7 */}
          <section>
            <h2 className="text-2xl font-bold mb-4">
              7. Sharing of Information
            </h2>

            <p className="text-slate-600 leading-8">
              We do not sell your personal data. We only share limited data with
              trusted third-party providers such as analytics, hosting, and email
              services required to operate our platform.
            </p>
          </section>

          {/* 8 */}
          <section>
            <h2 className="text-2xl font-bold mb-4">
              8. GDPR / International Users
            </h2>

            <p className="text-slate-600 leading-8">
              Users in the European Economic Area (EEA) may have rights under GDPR,
              including access, correction, deletion, and restriction of personal data.
            </p>
          </section>

          {/* 9 */}
          <section>
            <h2 className="text-2xl font-bold mb-4">
              9. California Privacy Rights
            </h2>

            <p className="text-slate-600 leading-8">
              California residents may request access, deletion, or correction of
              their personal data under CCPA/CPRA regulations.
            </p>
          </section>

          {/* 10 */}
          <section>
            <h2 className="text-2xl font-bold mb-4">10. Data Security</h2>

            <p className="text-slate-600 leading-8">
              We implement reasonable security measures to protect your data.
              However, no online system is 100% secure.
            </p>
          </section>

          {/* 11 */}
          <section>
            <h2 className="text-2xl font-bold mb-4">
              11. Children's Privacy
            </h2>

            <p className="text-slate-600 leading-8">
              Our website is not intended for children under 13. We do not
              knowingly collect personal information from children.
            </p>
          </section>

          {/* 12 */}
          <section>
            <h2 className="text-2xl font-bold mb-4">
              12. Third-Party Links
            </h2>

            <p className="text-slate-600 leading-8">
              Our website may contain links to third-party services such as
              airlines, banks, and travel providers. We are not responsible for
              their privacy practices.
            </p>
          </section>

          {/* 13 */}
          <section>
            <h2 className="text-2xl font-bold mb-4">
              13. Data Retention
            </h2>

            <p className="text-slate-600 leading-8">
              We retain personal data only as long as necessary for operational,
              legal, and security purposes.
            </p>
          </section>

          {/* 14 */}
          <section>
            <h2 className="text-2xl font-bold mb-4">
              14. Updates to This Policy
            </h2>

            <p className="text-slate-600 leading-8">
              We may update this Privacy Policy periodically. All updates will be
              posted on this page with a revised date.
            </p>
          </section>

          {/* 15 */}
          <section>
            <h2 className="text-2xl font-bold mb-4">
              15. Contact Information
            </h2>

            <div className="bg-slate-50 border border-slate-200 rounded-2xl p-5">
              <p className="text-voyage-cyan font-semibold">
                jfpcustomersupport@gmail.com
              </p>
            </div>
          </section>
        </div>

        {/* Footer */}
        <div className="text-center mt-10 text-sm text-slate-500">
          By using this website, you agree to this Privacy Policy.
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;